import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Fab,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { fetchInspections, deleteInspection } from '@store/slices/inspectionsSlice';
import InspectionCard from '@components/inspections/InspectionCard';
import { Inspection } from '@types';

interface InspectionFilters {
    search: string;
    hive: number | '';
    apiary: number | '';
    checklist: number | '';
    dateFrom: string;
    dateTo: string;
}

const Inspections: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { inspections, loadingState, error } = useAppSelector((state) => state.inspections);
    const { hives, apiaries } = useAppSelector((state) => state.locations);
    const { checklists } = useAppSelector((state) => state.checklists);

    const [filters, setFilters] = useState<InspectionFilters>({
        search: '',
        hive: searchParams.get('hive') ? parseInt(searchParams.get('hive')!) : '',
        apiary: '',
        checklist: '',
        dateFrom: '',
        dateTo: '',
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [inspectionToDelete, setInspectionToDelete] = useState<number | null>(null);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        const hiveId = searchParams.get('hive');
        dispatch(fetchInspections({ hiveId: hiveId ? parseInt(hiveId) : undefined }));
    }, [dispatch, searchParams]);

    const filteredInspections = (inspections || []).filter((inspection) => {
        // Search filter
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch =
                inspection.notes?.toLowerCase().includes(searchLower) ||
                inspection.hive?.name.toLowerCase().includes(searchLower) ||
                inspection.checklist?.name.toLowerCase().includes(searchLower) ||
                inspection.weather?.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
        }

        // Hive filter
        if (filters.hive && inspection.hive_id !== filters.hive) {
            return false;
        }

        // Apiary filter
        if (filters.apiary && inspection.hive?.location_id !== filters.apiary) {
            return false;
        }

        // Checklist filter
        if (filters.checklist && inspection.checklist_id !== filters.checklist) {
            return false;
        }

        // Date filters
        if (filters.dateFrom) {
            const inspectionDate = new Date(inspection.date);
            const fromDate = new Date(filters.dateFrom);
            if (inspectionDate < fromDate) return false;
        }

        if (filters.dateTo) {
            const inspectionDate = new Date(inspection.date);
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999); // End of day
            if (inspectionDate > toDate) return false;
        }

        return true;
    });

    const handleCreateInspection = () => {
        const hiveId = searchParams.get('hive');
        if (hiveId) {
            navigate(`/inspections/create?hiveId=${hiveId}`);
        } else {
            navigate('/inspections/create');
        }
    };

    const handleViewInspection = (inspection: Inspection) => {
        navigate(`/inspections/${inspection.id}`);
    };

    const handleEditInspection = (inspection: Inspection) => {
        navigate(`/hives/${inspection.hive_id}/inspections/${inspection.id}`);
    };

    const handleDeleteClick = (inspectionId: number) => {
        setInspectionToDelete(inspectionId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (inspectionToDelete) {
            await dispatch(deleteInspection(inspectionToDelete));
            setDeleteDialogOpen(false);
            setInspectionToDelete(null);
        }
    };

    const handleFilterChange = (field: keyof InspectionFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleClearSearch = () => {
        setFilters((prev) => ({ ...prev, search: '' }));
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            hive: '',
            apiary: '',
            checklist: '',
            dateFrom: '',
            dateTo: '',
        });
    };

    const showPlaceholder = filteredInspections.length === 0 && loadingState === 'succeeded';
    const hasActiveFilters =
        filters.search || filters.hive || filters.apiary || filters.checklist || filters.dateFrom || filters.dateTo;

    return (
        <Box>
            <Container sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 3, md: 4 }, px: { xs: 1.5, md: 3 } }}>
                {loadingState === 'pending' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : showPlaceholder ? (
                    <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
                        <Typography variant="h4" gutterBottom color="primary" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            {hasActiveFilters ? 'No Inspections Found' : 'No Inspections Yet'}
                        </Typography>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: { xs: 2, md: 4 }, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                            {hasActiveFilters
                                ? 'Try adjusting your filters to see more results.'
                                : 'Start tracking your hive inspections by creating your first inspection.'}
                        </Typography>
                        {!hasActiveFilters && (
                            <Button
                                variant="contained"
                                size={isMobile ? 'medium' : 'large'}
                                startIcon={<AddIcon />}
                                onClick={handleCreateInspection}
                                sx={{
                                    backgroundColor: '#F8B133',
                                    '&:hover': { backgroundColor: '#f29100' },
                                }}
                            >
                                Create First Inspection
                            </Button>
                        )}
                        {hasActiveFilters && (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Search and Filter Bar */}
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                    {/* Search */}
                                    <Grid item xs={12} md={showFilters ? 12 : 8}>
                                        <TextField
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            placeholder="Search inspections..."
                                            size="small"
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: filters.search && (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={handleClearSearch}>
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    {/* Filter Toggle Button */}
                                    {!showFilters && (
                                        <Grid item xs={12} md={4}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<FilterIcon />}
                                                onClick={() => setShowFilters(true)}
                                                sx={{ height: '40px' }}
                                            >
                                                More Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
                                            </Button>
                                        </Grid>
                                    )}

                                    {/* Expanded Filters */}
                                    {showFilters && (
                                        <>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Filter by Hive</InputLabel>
                                                    <Select
                                                        value={filters.hive}
                                                        label="Filter by Hive"
                                                        onChange={(e) => handleFilterChange('hive', e.target.value)}
                                                    >
                                                        <MenuItem value="">All Hives</MenuItem>
                                                        {hives.map((hive) => (
                                                            <MenuItem key={hive.id} value={hive.id}>
                                                                {hive.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Filter by Apiary</InputLabel>
                                                    <Select
                                                        value={filters.apiary}
                                                        label="Filter by Apiary"
                                                        onChange={(e) => handleFilterChange('apiary', e.target.value)}
                                                    >
                                                        <MenuItem value="">All Apiaries</MenuItem>
                                                        {apiaries.map((apiary) => (
                                                            <MenuItem key={apiary.id} value={apiary.id}>
                                                                {apiary.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Filter by Checklist</InputLabel>
                                                    <Select
                                                        value={filters.checklist}
                                                        label="Filter by Checklist"
                                                        onChange={(e) => handleFilterChange('checklist', e.target.value)}
                                                    >
                                                        <MenuItem value="">All Checklists</MenuItem>
                                                        {checklists.map((checklist) => (
                                                            <MenuItem key={checklist.id} value={checklist.id}>
                                                                {checklist.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleClearFilters}
                                                        fullWidth
                                                    >
                                                        Clear
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        onClick={() => setShowFilters(false)}
                                                        fullWidth
                                                    >
                                                        Hide
                                                    </Button>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    type="date"
                                                    label="From Date"
                                                    value={filters.dateFrom}
                                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    type="date"
                                                    label="To Date"
                                                    value={filters.dateTo}
                                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Inspections Grid */}
                        <Grid container spacing={3}>
                            {filteredInspections.map((inspection) => (
                                <Grid item xs={12} sm={6} lg={4} key={inspection.id}>
                                    <InspectionCard
                                        inspection={inspection}
                                        onView={handleViewInspection}
                                        onEdit={handleEditInspection}
                                        onDelete={handleDeleteClick}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {/* Floating Action Button */}
                <Fab
                    color="primary"
                    sx={{
                        position: 'fixed',
                        bottom: isMobile ? 80 : 24,
                        right: 24,
                        backgroundColor: '#F8B133',
                        '&:hover': { backgroundColor: '#f29100' },
                    }}
                    onClick={handleCreateInspection}
                >
                    <AddIcon />
                </Fab>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Delete Inspection</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete this inspection? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirmDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default Inspections;
